import React, { useEffect, memo } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { useTheme } from 'styled-components'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { IReduxActions, IReduxReducers } from '../../../../../../../store'
import { IBarrier, IBarrierTypeIcon, IConsequence, IThreat } from '../../../../../../../types'
import { Icon } from '../../../../../../../utils'
import {
    barrierWidth,
    threatConsequenceWidth,
    initialThreat,
    initialConsequence,
    threatConsequenceHeight,
    barrierEditorGap,
    unfocusOpacity,
    THREAT_COSNEQUENCE_GAP_X,
    THRAT_CONSEQUENCE_START_CURVE_GAP
} from '../../../../../../../utils/constants'
import { BarrierEditor, AddButton } from './components'
import { TextContainer, PopupSVG, DeleteBottomContainer, ThreatConsequenceContainer } from './styles'

interface Props {
    uid: string
    y: number
    type: IType
    threatConsequenceData: IThreat | IConsequence
    middleY: number
    x: number
    rootXIndex: number
    onEditClick: (barrier: IBarrier, type: IType, escId: string) => void
    onDeleteClick: () => void
    selectedBarrier: IBarrier
    handleCloseAllProcedure: (type: IType, escId: string) => void
    closeAll?: string
    setCloseAll: any
    onEditThreatConsequence: (item: IThreat | IConsequence, type: IType) => void
    selectedThreatConsequence: IThreat | IConsequence
    handleDeleteThreatConsequenceClick: (type: IType, index: number) => void
    canDelete: boolean
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
    showFrequency: boolean
}
type IType = 'threat' | 'consequence'

const ThreatandConsequenceEditor: React.FC<Props> = ({
    uid,
    type,
    y,
    threatConsequenceData,
    middleY,
    x,
    rootXIndex,
    onEditClick,
    onDeleteClick,
    selectedBarrier,
    handleCloseAllProcedure,
    closeAll,
    setCloseAll,
    selectedThreatConsequence,
    onEditThreatConsequence,
    handleDeleteThreatConsequenceClick,
    canDelete,
    barrierTypeIconMap,
    showFrequency
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const store = useStore()
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const { consequencesExpandedBarriers, threatsExpandedBarriers } = useSelector(
        (state: IReduxReducers) => state.bowtieExpandedBarriers
    )

    const addBarrierTargetUid = useSelector((state: IReduxReducers) => state.addBarrierEditor.targetUid)

    const openBrothersInside = (type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers).filter(el =>
        el.barrierId.startsWith(uid + '|')
    )

    useEffect(() => {
        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: type === 'threat' ? 'ADD_THREAT_EXPANDED_BARRIERS' : 'ADD_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: [
                    {
                        barrierId: uid,
                        rootXIndex,
                        escalations: [
                            {
                                escId: uid,
                                y: y,
                                x:
                                    type === 'threat'
                                        ? initialThreat -
                                          barrierWidth -
                                          (threatConsequenceData.barriers?.length - 1 ?? 0) *
                                              (barrierWidth + barrierEditorGap)
                                        : initialConsequence +
                                          barrierWidth +
                                          (threatConsequenceData.barriers?.length - 1 ?? 0) *
                                              (barrierWidth + barrierEditorGap)
                            }
                        ]
                    }
                ]
            }
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (addBarrierTargetUid != null && addBarrierTargetUid === uid) {
            addBarrier()
            dispatch<IReduxActions['addBarrierEditor']>({
                type: 'CLEAR_ADD_BARRIER_TARGET'
            })
        }
    }, [addBarrierTargetUid])

    useEffect(() => {
        const expandedBarrier = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

        if (expandedBarrier.length > 0) {
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: expandedBarrier.map(e => {
                        if (e.barrierId === uid) {
                            e.escalations = e.escalations.map(es => ({
                                ...es,
                                y: y,
                                x:
                                    type === 'threat'
                                        ? initialThreat -
                                          barrierWidth -
                                          (threatConsequenceData.barriers?.length - 1 ?? 0) *
                                              (barrierWidth + barrierEditorGap)
                                        : initialConsequence +
                                          barrierWidth +
                                          (threatConsequenceData.barriers?.length - 1 ?? 0) *
                                              (barrierWidth + barrierEditorGap)
                            }))
                        }

                        return e
                    })
                }
            })
        }
    }, [y, threatConsequenceData.barriers]) // eslint-disable-line react-hooks/exhaustive-deps

    const addBarrier = (): void => {
        handleCloseAllProcedure(type, `${uid}|bar`)

        threatConsequenceData.barriers.unshift({
            id: null,
            name: '',
            supervisor: undefined,
            barrierType: undefined,
            criticality: undefined,
            root: true,
            escalations: [],
            method: null,
            manualInteraction: null
        })

        onEditClick(threatConsequenceData.barriers[0], type, `${uid}|bar-new`)
        // TO-DO => NEEDS REFACTORING IN THE WHOLE STRUCTURE OF BARRIER REDUX AND STATE CHANGES, DONT TOUCH IN THIS TIMOUT OR IT'LL BREAK THE EXPANSION
        setTimeout(() => {
            const expandedBarrier =
                type === 'threat'
                    ? store.getState().bowtieExpandedBarriers.threatsExpandedBarriers
                    : store.getState().bowtieExpandedBarriers.consequencesExpandedBarriers
            const newExpanded = expandedBarrier.map(el => {
                if (el.barrierId === uid) {
                    return {
                        barrierId: uid,
                        rootXIndex,
                        escalations: [
                            {
                                escId: uid,
                                y: y,
                                x:
                                    type === 'threat'
                                        ? initialThreat -
                                          barrierWidth -
                                          (threatConsequenceData.barriers?.length - 1 ?? 0) *
                                              (barrierWidth + barrierEditorGap)
                                        : initialConsequence +
                                          barrierWidth +
                                          (threatConsequenceData.barriers?.length - 1 ?? 0) *
                                              (barrierWidth + barrierEditorGap)
                            }
                        ]
                    }
                }

                return el
            })

            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: newExpanded
                }
            })

            setCloseAll(undefined)
        }, 0)
    }

    const handleDeleteBarrier = (barrierData: IBarrier): void => {
        handleCloseAllProcedure(type, `${uid}|bar`)
        const newBarriers = threatConsequenceData?.barriers?.filter(bar => bar !== barrierData)
        threatConsequenceData.barriers = newBarriers
        onDeleteClick()
        setTimeout(() => {
            setCloseAll(undefined)
        }, 0)
    }

    return (
        <>
            {/* dashed line from pulse to end of threat or consequence  */}
            <path
                d={`M 0 ${middleY} L ${
                    type === 'threat'
                        ? initialThreat + THRAT_CONSEQUENCE_START_CURVE_GAP
                        : initialConsequence - THRAT_CONSEQUENCE_START_CURVE_GAP
                } ${y} H ${type === 'threat' ? x - THREAT_COSNEQUENCE_GAP_X : x + THREAT_COSNEQUENCE_GAP_X}`}
                strokeWidth={2}
                stroke={theme['light-borders']}
                strokeDasharray="5,5"
                fill="none"
            />
            <ThreatConsequenceContainer
                transform={`translate(${
                    type === 'threat'
                        ? x - threatConsequenceWidth - THREAT_COSNEQUENCE_GAP_X
                        : x + THREAT_COSNEQUENCE_GAP_X
                }, ${y - threatConsequenceHeight / 2 - (type === 'consequence' ? 2 : 0)})`}
                id={uid}
                cursor="pointer"
                opacity={
                    selectedBarrier ||
                    (selectedThreatConsequence && selectedThreatConsequence !== threatConsequenceData)
                        ? unfocusOpacity
                        : 1
                }
            >
                {canDelete && (
                    <g transform={`translate(${type === 'threat' ? -threatConsequenceWidth - 2 : 0},0)`}>
                        <rect
                            x={threatConsequenceWidth - 20}
                            width={50}
                            height={threatConsequenceHeight / 4}
                            fill="transparent"
                        />
                        {bowtieAdmin && (
                            <DeleteBottomContainer>
                                <Icon
                                    type="delete"
                                    color={theme['gray-200']}
                                    scale="1.5"
                                    auxliarColor={theme['red-500']}
                                    x={type === 'threat' ? threatConsequenceWidth - 22 : threatConsequenceWidth + 5}
                                    clickEvent={() => handleDeleteThreatConsequenceClick(type, rootXIndex)}
                                />

                                <PopupSVG
                                    transform={`translate(${type === 'threat' ? 45 : threatConsequenceWidth + 35}, -4)`}
                                >
                                    <g transform={`translate(${type === 'threat' ? 82 : 2}, 4)`}>
                                        <rect
                                            className="svg-popup-helper"
                                            height={16}
                                            width={16}
                                            transform="rotate(45)"
                                            rx={2}
                                            ry={2}
                                        />
                                    </g>
                                    <foreignObject
                                        transform={type === 'threat' ? 'translate(-65 0)' : ''}
                                        width={150}
                                        height={30}
                                    >
                                        <div className={type} style={type === 'threat' ? { marginLeft: 'auto' } : {}}>
                                            {`${i18n('Delete')} ${i18n(type)}`}
                                        </div>
                                    </foreignObject>
                                </PopupSVG>
                            </DeleteBottomContainer>
                        )}
                    </g>
                )}
                <g onClick={() => onEditThreatConsequence(threatConsequenceData, type)}>
                    {type === 'threat' ? (
                        <>
                            <path
                                d="M 157.32 67.422 L 143.904 125.11 C 143.405 127.256 141.211 128.998 139.008 128.998 L 3.992 128.998 C 1.789 128.998 0 127.21 0 125.006 L 0 3.994 C 0 1.79 1.789 0.002 3.992 0.002 L 139.008 0.002 C 141.211 0.002 143.405 1.744 143.904 3.89 L 157.32 61.578 C 157.696 63.191 157.696 65.809 157.32 67.422 Z"
                                fill="#3866B1"
                            />
                        </>
                    ) : (
                        <>
                            <path
                                d="M 0.68 61.578 L 14.096 3.89 C 14.595 1.744 16.789 0.002 18.992 0.002 L 154.008 0.002 C 156.211 0.002 158 1.79 158 3.994 L 158 125.006 C 158 127.21 156.211 128.998 154.008 128.998 L 18.992 128.998 C 16.789 128.998 14.595 127.256 14.096 125.11 L 0.68 67.422 C 0.304 65.809 0.304 63.191 0.68 61.578 Z"
                                fill="#8768A3"
                            />
                        </>
                    )}

                    <foreignObject
                        x={type === 'threat' ? 10 : 25}
                        y={8}
                        width={threatConsequenceWidth - 33}
                        height={threatConsequenceHeight - 33}
                        style={{ overflow: 'hidden' }}
                    >
                        <TextContainer>{threatConsequenceData?.name ?? i18n(type)}</TextContainer>
                    </foreignObject>
                    {showFrequency && (
                        <>
                            <text
                                x={type === 'threat' ? 10 : 25}
                                y={threatConsequenceHeight - 28}
                                textAnchor="start"
                                dominantBaseline="hanging"
                                fill="#fff"
                                fontWeight={400}
                                fontSize={11}
                            >
                                {type === 'threat'
                                    ? `${i18n('Initial Freq.')}: ${Number(
                                          (threatConsequenceData as IThreat).initialFrequency
                                      )?.toExponential(4) ?? '---'}`
                                    : `${i18n('Threshold Freq.')}: ${Number(
                                          (threatConsequenceData as IConsequence).thresholdFrequency
                                      )?.toExponential(4) ?? '---'}`}
                            </text>
                            <text
                                x={type === 'threat' ? 10 : 25}
                                y={threatConsequenceHeight - 15}
                                textAnchor="start"
                                dominantBaseline="hanging"
                                fill="#fff"
                                fontWeight={400}
                                fontSize={11}
                            >
                                {i18n('Final Freq.')} ---
                            </text>
                        </>
                    )}
                </g>
            </ThreatConsequenceContainer>

            <AddButton
                onAddRequest={addBarrier}
                x={type === 'consequence' ? initialConsequence - barrierWidth : initialThreat + barrierWidth / 2}
                y={y - 21}
                barrierLength={threatConsequenceData.barriers.length}
                type={type}
            />

            {threatConsequenceData?.barriers?.map((child, index) => {
                const barrierX =
                    type === 'threat'
                        ? initialThreat - barrierWidth - (barrierWidth + barrierEditorGap) * index
                        : initialConsequence + barrierWidth + (barrierWidth + barrierEditorGap) * index
                const barrierOpenCount =
                    openBrothersInside
                        .filter(el => el.rootXIndex > index)
                        .map(el => (el.escalations || []).length)
                        .reduce((a, b) => a + b, 0) || 0

                return (
                    <BarrierEditor
                        key={`${uid}|bar-${child.id}`}
                        x={barrierX}
                        y={y + 10}
                        barrierData={child}
                        type={type}
                        escId={`${uid}|bar-${child?.id}`}
                        rootXIndex={index}
                        barrierBrothersY={barrierOpenCount}
                        onEditClick={onEditClick}
                        onDeleteClick={handleDeleteBarrier}
                        isPrimary={true}
                        selectedBarrier={selectedBarrier}
                        selectedThreatConsequence={selectedThreatConsequence}
                        closeAll={closeAll}
                        barrierTypeIconMap={barrierTypeIconMap}
                    />
                )
            })}
        </>
    )
}

export default memo(ThreatandConsequenceEditor)
